import { useState } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import {
  Check,
  ChevronDown,
  Grid3X3,
  Cpu,
  Battery,
  Zap,
  Router,
  HardDrive,
  Activity,
  Shield,
  HelpCircle,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import { Button } from '@/components/ui/button.tsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.tsx';
import { cn } from '@/utils/utils.ts';
import { DeviceTypeNames } from '../schemas/devices.schemas';

type DeviceType = DeviceTypeNames | 'all';

const deviceTypeOptions = [
  {
    value: 'all' as DeviceType,
    label: 'All Devices',
    icon: Grid3X3,
    description: 'Show all device types',
  },
  {
    value: 'Inverter' as DeviceType,
    label: 'Inverter',
    icon: Zap,
    description: 'Power conversion devices',
  },
  {
    value: 'BMS' as DeviceType,
    label: 'BMS',
    icon: Shield,
    description: 'Battery Management Systems',
  },
  {
    value: 'SmartMeter' as DeviceType,
    label: 'Smart Meter',
    icon: Activity,
    description: 'Energy monitoring devices',
  },
  {
    value: 'SmartLogger' as DeviceType,
    label: 'Smart Logger',
    icon: Cpu,
    description: 'Advanced data logging devices',
  },
  {
    value: 'Dongle' as DeviceType,
    label: 'Dongle',
    icon: Router,
    description: 'Communication dongles',
  },
  {
    value: 'Logger' as DeviceType,
    label: 'Logger',
    icon: HardDrive,
    description: 'Data logging devices',
  },
  {
    value: 'PQConnect' as DeviceType,
    label: 'PQ Connect',
    icon: Activity,
    description: 'Power quality monitoring',
  },
  {
    value: 'Battery' as DeviceType,
    label: 'Battery',
    icon: Battery,
    description: 'Energy storage devices',
  },
  {
    value: 'Unknown' as DeviceType,
    label: 'Unknown',
    icon: HelpCircle,
    description: 'Unidentified device types',
  },
];

export type DevicesListFilterState = {
  deviceType: DeviceType;
};

interface DevicesListFilterCommandProps {
  onStateChange: (state: DevicesListFilterState) => void;
  currentState: DevicesListFilterState;
}

export const DevicesListFilterCommand = ({
  onStateChange,
  currentState,
}: DevicesListFilterCommandProps) => {
  const isMobile = useMediaQuery('(max-width:640px)');
  const [open, setOpen] = useState(false);

  const handleDeviceTypeChange = (deviceType: DeviceType) => {
    const newState = { ...currentState, deviceType };
    onStateChange(newState);
    setOpen(false);
  };

  const getButtonLabel = () => {
    const deviceLabel =
      deviceTypeOptions.find((d) => d.value === currentState.deviceType)?.label ||
      'All Devices';
    return deviceLabel;
  };

  const getButtonIcon = () => {
    const deviceOption = deviceTypeOptions.find((d) => d.value === currentState.deviceType);

    if (!deviceOption) return <Grid3X3 className="h-4 w-4 text-gray-600" />;

    const Icon = deviceOption.icon;
    return <Icon className="h-4 w-4 text-gray-600" />;
  };

  return (
    <div className="flex">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-auto min-w-[215px] justify-between rounded-md border border-gray-200 bg-white/80 px-4 py-2.5 transition-all duration-200 hover:bg-white/90 hover:shadow-md"
          >
            <div className="flex items-center gap-2">
              {getButtonIcon()}
              <span className="text-sm font-medium text-gray-700">{getButtonLabel()}</span>
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 rounded-xl border border-gray-200/50 bg-white/95 p-0 shadow-xl backdrop-blur-md"
          align="start"
          onOpenAutoFocus={(e) => isMobile && e.preventDefault()}
        >
          <Command className="rounded-xl">
            <CommandInput
              placeholder="Search device types..."
              className="border-none text-sm focus:ring-0"
            />
            <CommandList>
              <CommandEmpty>No device types found.</CommandEmpty>

              <CommandGroup heading="Device Type">
                {deviceTypeOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = currentState.deviceType === option.value;
                  const isAllOption = option.value === 'all';

                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => handleDeviceTypeChange(option.value)}
                      className={cn(
                        'mx-1 flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-gray-50',
                        isAllOption && 'mb-2 border border-gray-200 bg-gray-50/50'
                      )}
                    >
                      <Icon
                        className={cn(
                          'mt-0.5 h-4 w-4',
                          isAllOption ? 'text-gray-700' : 'text-gray-500'
                        )}
                      />
                      <div className="flex-1">
                        <div
                          className={cn('text-sm font-medium', isAllOption && 'text-gray-900')}
                        >
                          {option.label}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          {option.description}
                        </div>
                      </div>
                      <Check
                        className={cn(
                          'mt-0.5 h-4 w-4',
                          isSelected ? 'text-blue-600 opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
